import { useEffect, useRef, useState } from "react";
import { FiAlertTriangle, FiCheckCircle, FiFileText, FiInfo, FiUpload } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import { ModalShell } from "@/components/ui/ModalShell";
import { AppButton, MessageBanner } from "@/components/ui/primitives";
import {
  confirmTransactionImport,
  previewTransactionImport,
  type TransactionImportPreview,
} from "@/services/transactionImportService";
import { getApiErrorMessage } from "@/utils/apiError";

const MAX_FILE_SIZE = 2 * 1024 * 1024;

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

export const TransactionCsvImportModal = ({ isOpen, onClose }: Props) => {
  const { t } = useTranslation("profile");
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<TransactionImportPreview | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [feedback, setFeedback] = useState<{ tone: "error" | "success"; message: string } | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setFile(null);
      setPreview(null);
      setIsLoading(false);
      setFeedback(null);
      if (inputRef.current) inputRef.current.value = "";
    }
  }, [isOpen]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextFile = event.target.files?.[0] ?? null;
    setPreview(null);
    setFeedback(null);

    if (!nextFile) {
      setFile(null);
      return;
    }
    if (!nextFile.name.toLowerCase().endsWith(".csv")) {
      setFile(null);
      setFeedback({ tone: "error", message: t("csvImport.errors.fileType") });
      event.target.value = "";
      return;
    }
    if (nextFile.size > MAX_FILE_SIZE) {
      setFile(null);
      setFeedback({ tone: "error", message: t("csvImport.errors.fileSize") });
      event.target.value = "";
      return;
    }
    setFile(nextFile);
  };

  const handlePreview = async () => {
    if (!file || isLoading) return;
    setIsLoading(true);
    setFeedback(null);
    try {
      const result = await previewTransactionImport(file);
      setPreview(result);
      if (result.status === "READY") {
        setFeedback({ tone: "success", message: t("csvImport.previewReady") });
      }
    } catch (error) {
      setPreview(null);
      setFeedback({ tone: "error", message: getApiErrorMessage(error, t("csvImport.errors.preview")) });
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!preview || preview.status !== "READY" || isLoading) return;
    setIsLoading(true);
    setFeedback(null);
    try {
      const result = await confirmTransactionImport(preview.import_id);
      setPreview(result);
      setFeedback({
        tone: "success",
        message: t("csvImport.complete", { created: result.created_count, duplicates: result.duplicate_count }),
      });
    } catch (error) {
      setFeedback({ tone: "error", message: getApiErrorMessage(error, t("csvImport.errors.confirm")) });
    } finally {
      setIsLoading(false);
    }
  };

  const canConfirm = preview?.status === "READY";
  const isCompleted = preview?.status === "COMPLETED";
  const hasValidPreview = canConfirm || isCompleted;
  const hasPreviewErrors = (preview?.error_count ?? 0) > 0;
  const PreviewIcon = hasPreviewErrors ? FiAlertTriangle : hasValidPreview ? FiCheckCircle : FiInfo;
  const previewToneClass = hasPreviewErrors
    ? "bg-[var(--app-danger-soft)]"
    : hasValidPreview
      ? "bg-[var(--app-success-soft)]"
      : "app-panel-soft";
  const previewIconClass = hasPreviewErrors
    ? "text-[var(--app-danger)]"
    : hasValidPreview
      ? "text-[var(--app-success)]"
      : "text-[var(--app-primary)]";
  const progressLabel = isCompleted
    ? t("csvImport.progress.complete")
    : canConfirm
      ? t("csvImport.progress.confirm")
      : t("csvImport.progress.review");

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      title={t("csvImport.title")}
      subtitle={t("csvImport.subtitle")}
      labelledBy="transaction-csv-import-title"
      maxWidthClassName="max-w-[35rem]"
      headerMeta={
        <span className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--app-muted-text)]">
          {progressLabel}
        </span>
      }
      footer={
        <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
          <AppButton type="button" tone="secondary" onClick={onClose} disabled={isLoading}>
            {isCompleted ? t("csvImport.close") : t("csvImport.cancel")}
          </AppButton>
          {!isCompleted ? (
            <AppButton
              type="button"
              tone="primary"
              onClick={canConfirm ? handleConfirm : handlePreview}
              disabled={!file || isLoading}
              isLoading={isLoading}
              leadingIcon={<FiUpload className="h-4 w-4" />}
            >
              {canConfirm ? t("csvImport.confirm") : t("csvImport.preview")}
            </AppButton>
          ) : null}
        </div>
      }
    >
      <div className="space-y-5 pt-1">
        <label className="app-panel-soft flex cursor-pointer items-center gap-3 p-4 transition hover:bg-[var(--app-surface-hover)]">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--app-surface)] text-[var(--app-primary)]"><FiFileText className="h-5 w-5" /></span>
          <span className="min-w-0 flex-1"><span className="block truncate text-sm font-semibold text-[var(--app-text)]">{file?.name ?? t("csvImport.selectFile")}</span><span className="mt-0.5 block text-xs text-[var(--app-muted-text)]">{t("csvImport.fileHint")}</span></span>
          <span className="text-sm font-semibold text-[var(--app-primary)]">{t("csvImport.browse")}</span>
          <input ref={inputRef} type="file" accept=".csv,text/csv,application/vnd.ms-excel" className="sr-only" onChange={handleFileChange} disabled={isLoading || isCompleted} />
        </label>

        <details className="app-panel-soft p-4">
          <summary className="cursor-pointer text-sm font-semibold text-[var(--app-text)]">
            {t("csvImport.formatDisclosure")}
          </summary>
          <div className="mt-4 space-y-4">
            <section>
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--app-primary)]">{t("csvImport.formatTitle")}</p>
              <code className="mt-3 block overflow-x-auto rounded-xl bg-[var(--app-surface)] px-3 py-3 text-xs leading-5 text-[var(--app-text)]">
                date,name,amount,type,account,category,description,external_id<br />
                2026-07-01,Supermercado,845.90,EXPENSE,Tarjeta principal,Alimentos,Compra semanal,mov-1
              </code>
            </section>

            <ul className="space-y-2 text-sm leading-5 text-[var(--app-text-secondary)]">
              {["ruleEncoding", "ruleHeaders", "ruleValues", "ruleNames", "ruleLimit"].map((key) => (
                <li key={key} className="flex gap-3">
                  <FiInfo className="mt-0.5 h-4 w-4 shrink-0 text-[var(--app-muted-text)]" />
                  {t(`csvImport.${key}`)}
                </li>
              ))}
            </ul>
          </div>
        </details>

        {feedback ? <MessageBanner tone={feedback.tone}>{feedback.message}</MessageBanner> : null}

        {preview ? (
          <section className={`rounded-[20px] p-4 ${previewToneClass}`}>
            <div className="flex items-start gap-3">
              <PreviewIcon className={`mt-0.5 h-5 w-5 shrink-0 ${previewIconClass}`} />
              <div>
                <p className="font-semibold text-[var(--app-text)]">
                  {t("csvImport.summary", {
                    rows: preview.row_count,
                    valid: preview.valid_row_count,
                    errors: preview.error_count,
                  })}
                </p>
                {preview.errors.length > 0 ? (
                  <ul className="mt-2 space-y-1 text-sm text-[var(--app-danger)]">
                    {preview.errors.slice(0, 5).map((error, index) => (
                      <li key={`${error.line_number}-${error.field}-${index}`}>
                        {t("csvImport.rowError", {
                          line: error.line_number ?? "—",
                          field: error.field ?? "CSV",
                          message: error.message,
                        })}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            </div>
          </section>
        ) : null}

      </div>
    </ModalShell>
  );
};
